import React, { useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid2,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import CorporateEventsService, {
  RegistrationCustomQuestion,
  RegistrationCustomQuestionOption,
} from '~/services/CorporateEventsService';

import { useArrayDiff } from '../../hooks/useArrayDiff';

interface RegistrationCustomQuestionDialogProps {
  eventId?: string;
  open: boolean;
  question?: RegistrationCustomQuestion | null;
  defaultSortOrder?: number;
  onClose: () => void;
  onSave: (question: RegistrationCustomQuestion) => void;
  isSaving?: boolean;
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'select', label: 'Select' },
  { value: 'multiselect', label: 'Multiselect' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
];

export default function RegistrationCustomQuestionDialog({
  eventId,
  open,
  question,
  defaultSortOrder = 1,
  onClose,
  onSave,
  isSaving = false,
}: RegistrationCustomQuestionDialogProps) {
  const [formData, setFormData] = useState({
    label: '',
    description: '',
    fieldName: '',
    fieldType: 'text',
    isRequired: false,
    sortOrder: 0,
    isSensitive: false,
  });

  const [options, setOptions] = useState<Array<RegistrationCustomQuestionOption>>([]);
  const [initialOptions, setInitialOptions] = useState<Array<RegistrationCustomQuestionOption>>([]);
  const previousOpenRef = useRef(false);

  const isEditMode = !!question;
  const shouldLoadOptions =
    open &&
    !!question?.id &&
    (question.fieldType === 'select' || question.fieldType === 'multiselect' || question.fieldType === 'radio');

  // Load options when dialog opens for editing a question that requires options
  const { data: loadedOptions = [], isLoading: isLoadingOptions } = useQuery({
    queryKey: ['corporate-events', 'registration-custom-question-options', question?.id],
    queryFn: () => {
      if (!question?.id) {
        throw new Error('Question ID is required');
      }
      return CorporateEventsService.getRegistrationCustomQuestionOptionsByQuestionId(question.id);
    },
    enabled: shouldLoadOptions,
  });

  useEffect(() => {
    if (open && !previousOpenRef.current) {
      if (question) {
        setFormData({
          label: question.label || '',
          description: question.description || '',
          fieldName: question.fieldName || '',
          fieldType: question.fieldType || 'text',
          isRequired: question.isRequired || false,
          sortOrder: question.sortOrder || 0,
          isSensitive: question.isSensitive || false,
        });
        const initialOpts = question.options || [];
        setOptions(initialOpts);
        setInitialOptions(initialOpts);
      } else {
        // Reset form for new question
        setFormData({
          label: '',
          description: '',
          fieldName: '',
          fieldType: 'text',
          isRequired: false,
          sortOrder: defaultSortOrder,
          isSensitive: false,
        });
        setOptions([]);
        setInitialOptions([]);
      }
    }
    // Reset when dialog closes
    if (!open && previousOpenRef.current) {
      setFormData({
        label: '',
        description: '',
        fieldName: '',
        fieldType: 'text',
        isRequired: false,
        sortOrder: 0,
        isSensitive: false,
      });
      setOptions([]);
      setInitialOptions([]);
    }
    previousOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, question?.id]);

  // Separate effect to update options when loadedOptions are fetched (only for edit mode with select/multiselect/radio)
  useEffect(() => {
    if (open && question && shouldLoadOptions && loadedOptions.length > 0) {
      setOptions(loadedOptions);
      setInitialOptions(loadedOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedOptions]);

  const generateFieldNameFromLabel = (label: string): string => {
    return label
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_') // Replace spaces with underscore
      .replace(/-/g, '_') // Replace hyphens with underscore
      .replace(/[^a-z0-9_]/g, '') // Remove any other special characters
      .replace(/_+/g, '_') // Replace multiple underscores with single underscore
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
  };

  const handleChange =
    (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFormData((prev) => {
        const updated = { ...prev, [field]: value };

        if (field === 'label' && !isEditMode && value) {
          const generatedFieldName = generateFieldNameFromLabel(value);
          if (!prev.fieldName || prev.fieldName === generateFieldNameFromLabel(prev.label)) {
            updated.fieldName = generatedFieldName;
          }
        }

        return updated;
      });
    };

  const handleCheckboxChange =
    (field: 'isRequired' | 'isSensitive') => (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setFormData((prev) => ({ ...prev, [field]: checked }));
    };

  const handleNumberChange = (field: 'sortOrder') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10) || 0;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddOption = () => {
    const newOption: RegistrationCustomQuestionOption = {
      label: '',
      value: '',
      sortOrder: options.length + 1,
    };
    setOptions([...options, newOption]);
  };

  const handleOptionChange =
    (index: number, field: 'label' | 'value' | 'sortOrder') => (e: React.ChangeEvent<HTMLInputElement>) => {
      const updatedOptions = [...options];
      if (field === 'sortOrder') {
        updatedOptions[index] = {
          ...updatedOptions[index],
          [field]: parseInt(e.target.value, 10) || 0,
        };
      } else if (field === 'label') {
        const newLabel = e.target.value;
        const currentOption = updatedOptions[index];
        // Auto-fill value using the same logic as fieldName generation
        const generatedValue = generateFieldNameFromLabel(newLabel);
        const shouldAutoFillValue =
          !currentOption.value || currentOption.value === generateFieldNameFromLabel(currentOption.label);
        updatedOptions[index] = {
          ...currentOption,
          label: newLabel,
          value: shouldAutoFillValue ? generatedValue : currentOption.value,
        };
      } else {
        updatedOptions[index] = {
          ...updatedOptions[index],
          [field]: e.target.value,
        };
      }
      setOptions(updatedOptions);
    };

  const handleRemoveOption = (index: number) => {
    const updatedOptions = options.filter((_, i) => i !== index);
    setOptions(updatedOptions);
  };

  const requiresOptions =
    formData.fieldType === 'select' || formData.fieldType === 'multiselect' || formData.fieldType === 'radio';

  // Calculate diff between initial and current options using custom hook
  const optionsDiff = useArrayDiff({
    current: requiresOptions ? options : [],
    initial: requiresOptions ? initialOptions : [],
    getId: (opt) => opt.id,
  });

  const handleSubmit = () => {
    if (!formData.label || !formData.fieldName) {
      return;
    }

    const questionData: RegistrationCustomQuestion = {
      ...(question?.id && { id: question.id }),
      ...(eventId && { eventId }),
      label: formData.label,
      description: formData.description,
      fieldName: formData.fieldName,
      fieldType: formData.fieldType,
      isRequired: formData.isRequired,
      sortOrder: formData.sortOrder,
      isSensitive: formData.isSensitive,
      options: requiresOptions ? options : undefined,
      optionsDiff: requiresOptions ? optionsDiff : undefined,
    };

    onSave(questionData);
  };

  const handleClose = () => {
    setFormData({
      label: '',
      description: '',
      fieldName: '',
      fieldType: 'text',
      isRequired: false,
      sortOrder: 0,
      isSensitive: false,
    });
    setOptions([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Question' : 'Create Question'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Label" value={formData.label} onChange={handleChange('label')} required />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Field Name"
                value={formData.fieldName}
                onChange={handleChange('fieldName')}
                required
                helperText="Internal field identifier (e.g., 'dietary_requirements')"
              />
            </Grid2>
            <Grid2 size={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                multiline
                rows={2}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Field Type"
                select
                value={formData.fieldType}
                onChange={handleChange('fieldType')}
                required
                disabled={isEditMode}
                helperText={isEditMode ? 'Field type cannot be changed after creation' : undefined}
              >
                {FIELD_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Sort Order"
                value={formData.sortOrder}
                onChange={handleNumberChange('sortOrder')}
              />
            </Grid2>
            <Grid2 size={12}>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={<Checkbox checked={formData.isRequired} onChange={handleCheckboxChange('isRequired')} />}
                  label="Required"
                />
                <FormControlLabel
                  control={<Checkbox checked={formData.isSensitive} onChange={handleCheckboxChange('isSensitive')} />}
                  label="Sensitive Data"
                />
              </Stack>
            </Grid2>
          </Grid2>

          {requiresOptions && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Options</Typography>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddOption} size="small">
                  Add Option
                </Button>
              </Box>
              {isLoadingOptions ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Loading options...
                </Typography>
              ) : options.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No options added. Add at least one option for this field type.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {options.map((option, index) => (
                    <Box key={index} display="flex" gap={2} alignItems="flex-start">
                      <TextField
                        fullWidth
                        label="Display Label"
                        value={option.label}
                        onChange={handleOptionChange(index, 'label')}
                        helperText="Text shown to users in the dropdown"
                        size="small"
                        required
                      />
                      <TextField
                        fullWidth
                        label="Internal Value"
                        value={option.value}
                        onChange={handleOptionChange(index, 'value')}
                        helperText="Value saved in database"
                        size="small"
                        required
                      />
                      <TextField
                        type="number"
                        label="Order"
                        value={option.sortOrder || index}
                        onChange={handleOptionChange(index, 'sortOrder')}
                        size="small"
                        sx={{ width: 150 }}
                      />
                      <IconButton onClick={() => handleRemoveOption(index)} color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          autoFocus
          disabled={!formData.label || !formData.fieldName || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSaving ? (isEditMode ? 'Updating...' : 'Creating...') : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
